/**
 * Invoicing Integrations Sync Policy (cross-domain read)
 *
 * Loads the account's provisioned invoicing integrations into the billing slice
 * so the billing-entity form's invoicing-integration selector has options. The
 * invoicing subdomain store is only mounted on the Integrations page, so we read
 * the data here via the same subscription pattern the erps subdomain uses (the
 * SDD-sanctioned "small query in the billing sync policy" cross-domain read).
 *
 * The integrations readmodel stamps each row's `capabilities`
 * (see `integrations.readmodel.ts`), so we keep only integrations whose row
 * declares `issues-invoices` — no second hop into the connectors catalog.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { AccountState } from '../state';
import { AccountCommand } from '../commands';

const declaresIssuesInvoices = (
  integration: SystemIntegrationsReadmodel | undefined
): boolean =>
  Boolean( integration?.capabilities?.includes( 'issues-invoices' ));

/**
 * Creates a policy that syncs the account's provisioned invoicing integrations
 * (whose readmodel row declares `issues-invoices`) into the billing slice.
 */
export const createInvoicingIntegrationsSyncPolicy = (
  backend: ClientLibraryType
): Policy<AccountState, AccountCommand> => ({
  name: 'invoicing-integrations-sync',

  init: ({ dispatch, getState }) => {
    const subscription = backend.subscriptions.ExternalSystemsIntegrations(
      {},
      ( change: ReadmodelChange<SystemIntegrationsReadmodel> ) => {
        if ( change.type === 'snapshot' ) {
          const invoicingRecord = change.data.items
            .filter( declaresIssuesInvoices )
            .reduce<Record<string, SystemIntegrationsReadmodel>>(
              ( acc, integration ) => {
                acc[integration.id] = integration;
                return acc;
              },
              {}
            );
          dispatch(
            {
              type: 'account/billing/data/setInvoicingIntegrations',
              payload: { integrations: invoicingRecord }
            },
            { source: 'api' }
          );
          return;
        }

        if ( change.type === 'signal' ) return;

        const record = change.type === 'delete' ? change.previous : change.data;
        if ( !declaresIssuesInvoices( record )) return;

        const current = { ...getState().billing.invoicingIntegrations };
        if ( change.type === 'delete' ) {
          delete current[change.previous.id];
        } else {
          current[change.data.id] = change.data;
        }
        dispatch(
          {
            type: 'account/billing/data/setInvoicingIntegrations',
            payload: { integrations: current }
          },
          { source: 'api' }
        );
      }
    );

    return () => {
      subscription?.close();
    };
  }
});
